import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';
import {
  Undo as ReturnIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { Sale, SaleItem, ReturnType, RefundMethod } from '../../utils/types';
import { formatCurrency } from '../../utils/numberUtils';
import { getCustomerCreditBalance } from '../../services/customerCreditService';
import { getAvailableReturnQuantities } from '../../services/posService';
import RefundMethodDialog from './RefundMethodDialog';

interface ReturnItem extends SaleItem {
  returnQuantity: number;
  returnReason: string;
  canReturn: boolean;
}

interface EnhancedReturnDialogProps {
  open: boolean;
  onClose: () => void;
  sale: Sale | null;
  onFullReturn: (saleId: string, reason: string, refundMethod: RefundMethod) => Promise<void>;
  onPartialReturn: (saleId: string, items: any[], reason: string, refundMethod: RefundMethod) => Promise<void>;
}

const EnhancedReturnDialog: React.FC<EnhancedReturnDialogProps> = ({
  open,
  onClose,
  sale,
  onFullReturn,
  onPartialReturn
}) => {
  const [returnType, setReturnType] = useState<ReturnType>(ReturnType.FULL);
  const [returnItems, setReturnItems] = useState<ReturnItem[]>([]);
  const [returnReason, setReturnReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customerCreditBalance, setCustomerCreditBalance] = useState(0);
  const [showRefundMethodDialog, setShowRefundMethodDialog] = useState(false);
  const [pendingReturnData, setPendingReturnData] = useState<any>(null);
  const [availableQuantities, setAvailableQuantities] = useState<Record<string, {
    originalQuantity: number;
    returnedQuantity: number;
    availableQuantity: number;
    itemName: string;
  }>>({});
  const [canCreateFullReturn, setCanCreateFullReturn] = useState(true);

  // تحضير عناصر الإرجاع عند فتح النافذة
  useEffect(() => {
    if (open && sale) {
      // تحميل الكميات المتاحة للإرجاع
      const loadAvailableQuantities = async () => {
        try {
          const quantities = await getAvailableReturnQuantities(sale.id);
          setAvailableQuantities(quantities);

          // التحقق من إمكانية الإرجاع الكامل
          const canFullReturn = Object.values(quantities).every(q => q.availableQuantity === q.originalQuantity);
          setCanCreateFullReturn(canFullReturn && sale.returnStatus !== 'FULL_RETURNED');

          // تحضير عناصر الإرجاع مع الكميات المتاحة
          const items: ReturnItem[] = sale.items.map(item => {
            const availableQty = quantities[item.itemId]?.availableQuantity || 0;
            return {
              ...item,
              returnQuantity: returnType === ReturnType.FULL ? availableQty : 0,
              returnReason: '',
              canReturn: availableQty > 0
            };
          });
          setReturnItems(items);
        } catch (error) {
          console.error('Error loading available quantities:', error);
          setError('حدث خطأ في تحميل بيانات الإرجاع');
        }
      };

      loadAvailableQuantities();
      setReturnReason('');
      setError(null);

      // جلب رصيد العميل إذا كان موجود
      if (sale.customerId) {
        loadCustomerCreditBalance(sale.customerId);
      }
    }
  }, [open, sale, returnType]);

  const loadCustomerCreditBalance = async (customerId: string) => {
    try {
      const balance = await getCustomerCreditBalance(customerId);
      setCustomerCreditBalance(balance);
    } catch (error) {
      console.error('Error loading customer credit balance:', error);
      setCustomerCreditBalance(0);
    }
  };

  const handleReturnTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newType = event.target.value as ReturnType;
    setReturnType(newType);
    
    // تحديث كميات الإرجاع حسب النوع
    const newItems = returnItems.map(item => ({
      ...item,
      returnQuantity: newType === ReturnType.FULL ? item.quantity : 0
    }));
    setReturnItems(newItems);
  };

  const handleQuantityChange = (index: number, quantity: number) => {
    const newItems = [...returnItems];
    const maxQuantity = newItems[index].quantity;
    newItems[index].returnQuantity = Math.max(0, Math.min(quantity, maxQuantity));
    setReturnItems(newItems);
  };

  const handleReasonChange = (index: number, reason: string) => {
    const newItems = [...returnItems];
    newItems[index].returnReason = reason;
    setReturnItems(newItems);
  };

  const getReturnSummary = () => {
    if (returnType === ReturnType.FULL) {
      return {
        itemsCount: sale?.items.length || 0,
        totalItems: sale?.items.reduce((sum, item) => sum + item.quantity, 0) || 0,
        totalAmount: sale?.grandTotal || 0
      };
    } else {
      const itemsToReturn = returnItems.filter(item => item.returnQuantity > 0);
      const totalAmount = itemsToReturn.reduce((sum, item) => 
        sum + (item.price * item.returnQuantity), 0
      );
      
      return {
        itemsCount: itemsToReturn.length,
        totalItems: itemsToReturn.reduce((sum, item) => sum + item.returnQuantity, 0),
        totalAmount
      };
    }
  };

  const handleSubmit = async () => {
    if (!sale) return;

    if (returnType === ReturnType.PARTIAL) {
      const itemsToReturn = returnItems.filter(item => item.returnQuantity > 0);
      
      if (itemsToReturn.length === 0) {
        setError('يجب اختيار منتج واحد على الأقل للإرجاع');
        return;
      }

      // التحقق من وجود أسباب الإرجاع
      const itemsWithoutReason = itemsToReturn.filter(item => !item.returnReason.trim());
      if (itemsWithoutReason.length > 0) {
        setError('يجب إدخال سبب الإرجاع لجميع المنتجات المختارة');
        return;
      }
    }

    if (!returnReason.trim()) {
      setError('يجب إدخال سبب الإرجاع');
      return;
    }

    // حفظ بيانات المرتجع وفتح نافذة اختيار طريقة الاسترداد
    setPendingReturnData({
      type: returnType,
      reason: returnReason,
      items: returnType === ReturnType.PARTIAL ? 
        returnItems.filter(item => item.returnQuantity > 0) : 
        null
    });
    setShowRefundMethodDialog(true);
  };

  const handleRefundMethodConfirm = async (refundMethod: RefundMethod) => {
    if (!sale || !pendingReturnData) return;

    setLoading(true);
    setError(null);
    setShowRefundMethodDialog(false);

    try {
      if (pendingReturnData.type === ReturnType.FULL) {
        await onFullReturn(sale.id, pendingReturnData.reason, refundMethod);
      } else {
        await onPartialReturn(sale.id, pendingReturnData.items, pendingReturnData.reason, refundMethod);
      }
      onClose();
    } catch (error) {
      console.error('Error processing return:', error);
      setError('حدث خطأ أثناء معالجة الإرجاع');
    } finally {
      setLoading(false);
      setPendingReturnData(null);
    }
  };

  const summary = getReturnSummary();
  const hasItemsToReturn = summary.itemsCount > 0;

  if (!sale) return null;

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        disableEscapeKeyDown={loading}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <ReturnIcon color="warning" />
            <Typography variant="h6">
              إرجاع منتجات - فاتورة {sale.invoiceNumber || sale.id.substring(0, 8)}
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Box py={1}>
            {/* معلومات الفاتورة */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2">
                    <strong>العميل:</strong> {sale.customerName || 'عميل نقدي'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2">
                    <strong>تاريخ البيع:</strong> {new Date(sale.createdAt).toLocaleDateString('ar-EG')}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2">
                    <strong>إجمالي الفاتورة:</strong> {formatCurrency(sale.grandTotal)}
                  </Typography>
                </Grid>
                {sale.customerId && (
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2">
                      <strong>رصيد العميل:</strong> {formatCurrency(customerCreditBalance)}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>

            {/* نوع المرتجع */}
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormLabel component="legend">نوع المرتجع:</FormLabel>
              <RadioGroup
                row
                value={returnType}
                onChange={handleReturnTypeChange}
              >
                <FormControlLabel
                  value={ReturnType.FULL}
                  control={<Radio disabled={loading || !canCreateFullReturn} />}
                  label={
                    <Box>
                      <Typography>مرتجع كامل</Typography>
                      {!canCreateFullReturn && (
                        <Typography variant="caption" color="error">
                          {sale?.returnStatus === 'FULL_RETURNED'
                            ? 'الفاتورة مرتجعة كلياً بالفعل'
                            : 'توجد مرتجعات جزئية سابقة'}
                        </Typography>
                      )}
                    </Box>
                  }
                />
                <FormControlLabel
                  value={ReturnType.PARTIAL}
                  control={<Radio disabled={loading || sale?.returnStatus === 'FULL_RETURNED'} />}
                  label={
                    <Box>
                      <Typography>مرتجع جزئي</Typography>
                      {sale?.returnStatus === 'FULL_RETURNED' && (
                        <Typography variant="caption" color="error">
                          الفاتورة مرتجعة كلياً بالفعل
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>

            {/* سبب الإرجاع */}
            <TextField
              label="سبب الإرجاع"
              multiline
              rows={2}
              fullWidth
              value={returnReason}
              onChange={(e) => setReturnReason(e.target.value)}
              placeholder="أدخل سبب الإرجاع..."
              sx={{ mb: 3 }}
              disabled={loading}
              required
            />

            {/* جدول المنتجات للمرتجع الجزئي */}
            {returnType === ReturnType.PARTIAL && (
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>المنتج</TableCell>
                      <TableCell align="center">الكمية الأصلية</TableCell>
                      <TableCell align="center">كمية الإرجاع</TableCell>
                      <TableCell align="center">السعر</TableCell>
                      <TableCell>سبب الإرجاع</TableCell>
                      <TableCell align="center">المجموع</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {returnItems.map((item, index) => (
                      <TableRow key={item.itemId}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {item.name}
                          </Typography>
                          {item.sku && (
                            <Typography variant="caption" color="text.secondary">
                              SKU: {item.sku}
                            </Typography>
                          )}
                          {availableQuantities[item.itemId] && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              متاح للإرجاع: {availableQuantities[item.itemId].availableQuantity} من {availableQuantities[item.itemId].originalQuantity}
                              {availableQuantities[item.itemId].returnedQuantity > 0 && (
                                <span style={{ color: '#f57c00' }}>
                                  {' '}(تم إرجاع {availableQuantities[item.itemId].returnedQuantity})
                                </span>
                              )}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={availableQuantities[item.itemId]?.availableQuantity || item.quantity}
                            size="small"
                            color={availableQuantities[item.itemId]?.availableQuantity === 0 ? 'error' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <TextField
                            type="number"
                            size="small"
                            value={item.returnQuantity}
                            onChange={(e) => handleQuantityChange(index, parseInt(e.target.value) || 0)}
                            inputProps={{
                              min: 0,
                              max: availableQuantities[item.itemId]?.availableQuantity || item.quantity
                            }}
                            sx={{ width: 80 }}
                            disabled={loading || !item.canReturn || (availableQuantities[item.itemId]?.availableQuantity || 0) === 0}
                            error={(availableQuantities[item.itemId]?.availableQuantity || 0) === 0}
                          />
                        </TableCell>
                        <TableCell align="center">
                          {formatCurrency(item.price)}
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            placeholder="سبب الإرجاع"
                            value={item.returnReason}
                            onChange={(e) => handleReasonChange(index, e.target.value)}
                            disabled={loading || item.returnQuantity === 0}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography 
                            variant="body2" 
                            fontWeight={item.returnQuantity > 0 ? 'bold' : 'normal'}
                            color={item.returnQuantity > 0 ? 'warning.main' : 'text.secondary'}
                          >
                            {formatCurrency(item.price * item.returnQuantity)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* ملخص الإرجاع */}
            {hasItemsToReturn && (
              <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'warning.light' }}>
                <Typography variant="subtitle1" fontWeight="bold" color="warning.dark" gutterBottom>
                  ملخص الإرجاع
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="warning.dark">
                      <strong>عدد المنتجات:</strong> {summary.itemsCount}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="warning.dark">
                      <strong>إجمالي القطع:</strong> {summary.totalItems}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="warning.dark">
                      <strong>إجمالي المبلغ:</strong> {formatCurrency(summary.totalAmount)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            )}

            {/* رسالة خطأ */}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* تحذيرات */}
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>ملاحظة:</strong> سيتم إرجاع المنتجات إلى المخزون تلقائياً، وستتمكن من اختيار طريقة الاسترداد (نقدي أو رصيد للعميل).
              </Typography>
            </Alert>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button 
            onClick={onClose}
            disabled={loading}
          >
            إلغاء
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="warning"
            disabled={loading || !hasItemsToReturn}
            startIcon={loading ? <CircularProgress size={20} /> : <ReturnIcon />}
          >
            {loading ? 'جاري المعالجة...' : `متابعة الإرجاع`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* نافذة اختيار طريقة الاسترداد */}
      <RefundMethodDialog
        open={showRefundMethodDialog}
        onClose={() => {
          setShowRefundMethodDialog(false);
          setPendingReturnData(null);
        }}
        onConfirm={handleRefundMethodConfirm}
        refundAmount={summary.totalAmount}
        customerName={sale?.customerName}
        customerCreditBalance={customerCreditBalance}
        loading={loading}
      />
    </>
  );
};

export default EnhancedReturnDialog;
